import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import Countdown from "@/components/Countdown";
import { toast } from "sonner";
import { Video, Calendar, Clock3, Download, ExternalLink, CheckCircle2, Sparkles } from "lucide-react";
import LiveWidget from "@/components/LiveWidget";

export default function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/portal/${token}`);
        setData(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Portal not found");
      }
    })();
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="vp-card p-8 max-w-md text-center">
        <div className="vp-label mb-2">Portal</div>
        <div className="font-display text-2xl font-medium">This link isn't active.</div>
        <p className="text-[#4A4D57] mt-2 text-sm">{err}</p>
        <Link to="/" className="vp-btn-ghost mt-6 inline-flex">Go home</Link>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;

  const { attendee, webinar, host, state, certificate_available, recording_url } = data;

  const stateLabel = { before: "Upcoming", live: "Live now", after: "Ended" }[state];
  const stateTint = { before: "#0B5CFF", live: "#E02D3C", after: "#4A4D57" }[state];

  return (
    <div className="min-h-screen">
      {/* Nav */}
      <nav className="max-w-[1200px] mx-auto flex items-center justify-between px-6 sm:px-10 py-6">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#F6F7FA] border border-[#0B5CFF]/40 flex items-center justify-center">
            <Video className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
          </div>
          <span className="font-display font-medium text-lg tracking-tight">Videoepreneur</span>
        </Link>
        <span className="vp-chip vp-chip-accent" style={{ color: stateTint, borderColor: stateTint + "60" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: stateTint }} />
          {stateLabel}
        </span>
      </nav>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-8 pb-16">
        <div className="vp-label">Attendee portal</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-[-0.028em] mt-2 max-w-3xl">
          Hi <span className="font-serif-italic text-[#0B5CFF]">{attendee.name.split(" ")[0]}</span> — everything you need is right here.
        </h1>
        <p className="text-[#4A4D57] mt-4 text-lg max-w-2xl">
          {webinar.title} · with {host.name}
        </p>

        {state === "before" && (
          <div className="mt-8">
            <div className="vp-label mb-3">Starts in</div>
            <Countdown target={webinar.scheduled_at} />
          </div>
        )}
      </section>

      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 pb-24 grid lg:grid-cols-3 gap-6">
        {/* Join card */}
        <div className="vp-card p-8 lg:col-span-2">
          <div className="vp-label">Your unique join link</div>
          <div className="mt-3 font-display text-2xl font-medium">{webinar.title}</div>
          <div className="flex items-center gap-4 mt-3 text-sm text-[#4A4D57] flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" strokeWidth={1.5} /> {new Date(webinar.scheduled_at).toLocaleString("en-IN")}</span>
            <span className="inline-flex items-center gap-1.5"><Clock3 className="w-4 h-4" strokeWidth={1.5} /> {webinar.duration_min} min</span>
          </div>

          <div className="vp-divider my-6" />

          {state !== "after" ? (
            <>
              <a href={attendee.join_url} target="_blank" rel="noreferrer" data-testid="portal-join-btn"
                 className="vp-btn-primary inline-flex items-center gap-2">
                Join Zoom <ExternalLink className="w-4 h-4" />
              </a>
              <div className="text-xs text-[#747680] mt-3 break-all">{attendee.join_url}</div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-[#4A4D57]">The session has ended. Recording and certificate below.</div>
              {recording_url ? (
                <a href={recording_url} target="_blank" rel="noreferrer" className="vp-btn-primary inline-flex items-center gap-2">
                  Watch recording <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <div className="vp-chip">Recording processing — you'll get an email when ready</div>
              )}
            </div>
          )}
        </div>

        {/* Live widget — only during the session */}
        {state === "live" && (
          <div className="lg:col-span-2">
            <LiveWidget portalToken={token} webinarId={webinar.id} />
          </div>
        )}

        {/* Host card + Certificate */}
        <div className="space-y-6">
          <div className="vp-card p-6">
            <div className="vp-label mb-3">Your host</div>
            <div className="font-display font-semibold text-lg">{host.name}</div>
            <p className="text-sm text-[#4A4D57] mt-1">{host.host_bio || "Sharing what actually works."}</p>
          </div>

          <div className="vp-card p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.5} />
              <div className="vp-label" style={{ color: "#0B5CFF" }}>Certificate</div>
            </div>
            <div className="font-display font-semibold text-lg">Certificate of participation</div>
            <p className="text-sm text-[#4A4D57] mt-1">Personalised PDF · downloads instantly</p>
            {certificate_available ? (
              <a
                href={`${API_BASE}/public/certificate/${token}.pdf`}
                target="_blank" rel="noreferrer"
                data-testid="portal-cert-download"
                className="vp-btn-primary mt-5 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            ) : (
              <div className="mt-5 text-sm text-[#747680]">
                Available once the webinar ends and payment is confirmed.
              </div>
            )}
          </div>

          <div className="vp-card p-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-[#1E8E3E]" strokeWidth={1.5} />
              <div className="vp-label" style={{ color: "#1E8E3E" }}>Registered</div>
            </div>
            <div className="text-sm text-[#0B0D17]">{attendee.email}</div>
            <div className="text-xs text-[#747680] mt-1">Payment: {attendee.payment_status}</div>
          </div>
        </div>
      </div>

      <footer className="border-t border-[#E4E6EC] py-8 text-center text-sm text-[#747680]">
        <div>© {new Date().getFullYear()} Videoepreneur</div>
      </footer>
    </div>
  );
}
